import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import { toast } from "react-toastify";
import {
  getConsentTemplate,
  getConsentTemplateMeta,
  publishConsentTemplate,
  saveConsentTemplateDesign,
  updateConsentTemplate,
} from "api/consentTemplate";
import { listSpecialities } from "api/patientHistoryMaster";
import ConsentContentEditor from "./ConsentContentEditor";
import ConsentBasicInfoForm from "./ConsentBasicInfoForm";
import ConsentFieldsEditor from "./ConsentFieldsEditor";
import ConsentPreviewDialog from "./ConsentPreviewDialog";
import { emptyDesign, fieldMappingsOf } from "./consentElements";
import {
  draftConsentFieldsOf,
  offeredConsentFieldSources,
  presetFieldsFor,
  withConsentFields,
} from "./consentSpecificFields";
import {
  ALL_SPECIALITIES_LABEL,
  applyContentBlocks,
  consentDetailsForm,
  consentDetailsPayload,
  contentBlocksOf,
  ensureClinicHeader,
  hasPrintableContent,
  specialityNames,
  validateConsentDetails,
} from "./consentSimpleModel";
import { statusMeta, typeLabelOf } from "./consentTemplateModel";
import "./Consent.css";

const apiMessage = (error, fallback) =>
  error?.response?.data?.message || fallback;

const ConsentBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [template, setTemplate] = useState(null);
  const [design, setDesign] = useState(emptyDesign());
  const [working, setWorking] = useState(null);
  const [meta, setMeta] = useState({});
  const [specialityOptions, setSpecialityOptions] = useState([]);

  /** Bumped on every read so the editor re-seeds from the loaded design. */
  const [seed, setSeed] = useState(0);

  const [details, setDetails] = useState(null);
  const [detailErrors, setDetailErrors] = useState({});

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [preview, setPreview] = useState(false);
  const [pendingLeave, setPendingLeave] = useState(null);

  const applyRead = useCallback((payload) => {
    setTemplate(payload.template);

    const read = payload.version.design;
    const filled = ensureClinicHeader(read);

    setDesign(filled);
    setWorking({
      version: payload.template.workingVersion,
      status: payload.template.workingVersionStatus,
    });
    setDirty(filled !== read);
    setSeed((value) => value + 1);
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    getConsentTemplate(id)
      .then((response) => applyRead(response.data.data))
      .catch((error) => {
        toast.error(apiMessage(error, "Could not open this consent."));
        navigate("/consent");
      })
      .finally(() => setLoading(false));
  }, [id, applyRead, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    getConsentTemplateMeta()
      .then((response) => setMeta(response.data.data))
      .catch(() => setMeta({}));
  }, []);

  useEffect(() => {
    let active = true;
    listSpecialities()
      .then((list) => active && setSpecialityOptions(list))
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  // The content only lives in the browser until Save Draft.
  useEffect(() => {
    const warn = (event) => {
      if (!dirty) return undefined;
      event.preventDefault();
      event.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  /** The editor's content → the design, header and footer untouched. */
  const handleContent = (blocks) => {
    setDesign((current) => applyContentBlocks(current, blocks));
    setDirty(true);
  };

  const handleFields = (fields) => {
    setDesign((current) => withConsentFields(current, fields));
    setDirty(true);
  };

  const saveDraft = async ({ silent } = {}) => {
    setBusy(true);
    try {
      const response = await saveConsentTemplateDesign(id, {
        design,
        fieldMappings: fieldMappingsOf(design),
        version: working?.version,
      });

      const payload = response.data.data;
      setTemplate(payload.template);
      setWorking({
        version: payload.template.workingVersion,
        status: payload.template.workingVersionStatus,
      });
      setDesign(payload.version.design);
      setDirty(false);

      if (!silent) toast.success("Consent saved");
      return true;
    } catch (error) {
      if (error?.response?.status === 409) {
        toast.error(apiMessage(error, "This consent has moved on. Reload it."));
      } else {
        toast.error(apiMessage(error, "Could not save this consent."));
      }
      return false;
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    if (!hasPrintableContent(design)) {
      toast.warning("Write the consent content before publishing.");
      return;
    }
    if (dirty && !(await saveDraft({ silent: true }))) return;

    setBusy(true);
    try {
      const response = await publishConsentTemplate(id);
      toast.success(response.data.message || "Published");
      load();
    } catch (error) {
      toast.error(apiMessage(error, "Could not publish this consent."));
    } finally {
      setBusy(false);
    }
  };

  const saveDetails = async () => {
    const found = validateConsentDetails(details);
    setDetailErrors(found);
    if (Object.keys(found).length) return;

    setBusy(true);
    try {
      const response = await updateConsentTemplate(
        id,
        consentDetailsPayload(details)
      );
      setTemplate(response.data.data.template);
      setDetails(null);
      toast.success("Consent updated");
    } catch (error) {
      toast.error(apiMessage(error, "Could not update this consent."));
    } finally {
      setBusy(false);
    }
  };

  const guard = (action) => {
    if (dirty) {
      setPendingLeave({ action });
      return;
    }
    action();
  };

  const status = statusMeta(template?.status);
  const names = specialityNames(template || {}, specialityOptions);

  if (loading && !template) {
    return (
      <Backdrop open sx={{ color: "#fff", zIndex: 1200 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <Box sx={{ pb: 2 }}>
      {/* ------------------------------------------------------- header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
          py: 1.5,
        }}
      >
        <IconButton
          aria-label="Back to consent forms"
          onClick={() => guard(() => navigate("/consent"))}
        >
          <ArrowBackIcon />
        </IconButton>

        <Box sx={{ mr: "auto" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
              {template?.name}
            </Typography>
            <Tooltip title="Edit specialities, name and format">
              <IconButton
                size="small"
                aria-label="Edit consent details"
                onClick={() =>
                  setDetails(consentDetailsForm(template || {}, specialityOptions))
                }
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", mt: 0.5, flexWrap: "wrap" }}>
            <Chip size="small" label={typeLabelOf(template || {})} />
            <Chip size="small" label={status.label} color={status.color} />
            <Chip
              size="small"
              variant="outlined"
              label={names.length ? names.join(", ") : ALL_SPECIALITIES_LABEL}
            />
            {dirty ? (
              <Chip size="small" color="warning" label="Unsaved changes" />
            ) : null}
          </Box>
        </Box>

        <Button variant="outlined" disabled={busy} onClick={() => saveDraft()}>
          Save
        </Button>
        <Button variant="outlined" onClick={() => setPreview(true)}>
          Preview
        </Button>
        <Tooltip title="Publish this consent and make it usable">
          <span>
            <Button variant="contained" disabled={busy} onClick={publish}>
              Publish / Activate
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* The editor is only for consent wording. The design still retains its
          clinic, patient and signature elements for Preview and Print. */}
      <Box className="consent-simple consent-simple--editor">
        <Box className="consent-sheet consent-sheet--builder">
          <Box className="consent-band consent-band--fields">
            <Typography className="consent-band__label consent-band__label--active">
              Consent-specific Fields
            </Typography>
            <ConsentFieldsEditor
              fields={draftConsentFieldsOf(design)}
              sources={offeredConsentFieldSources(meta?.consentFieldSources)}
              presets={presetFieldsFor(template?.type, meta?.consentFieldPresets)}
              onChange={handleFields}
            />
          </Box>
          <Box className="consent-band consent-band--content">
            <Typography className="consent-band__label consent-band__label--active">
              Consent Content
            </Typography>
            <ConsentContentEditor
              key={seed}
              blocks={contentBlocksOf(design)}
              onChange={handleContent}
            />
          </Box>
        </Box>
      </Box>

      <ConsentPreviewDialog
        open={preview}
        onClose={() => setPreview(false)}
        template={template}
        design={design}
        contentOnly
      />

      {/* --------------------------------------------- details, in a dialog */}
      <Dialog
        open={Boolean(details)}
        onClose={() => setDetails(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Consent details</DialogTitle>
        <DialogContent dividers>
          {details ? (
            <ConsentBasicInfoForm
              value={details}
              errors={detailErrors}
              meta={meta}
              specialityOptions={specialityOptions}
              onChange={setDetails}
            />
          ) : null}
          <Alert severity="info" sx={{ mt: 2 }}>
            Status is changed from the consent list (Activate / Deactivate), and
            a consent can only be activated once it has been published.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetails(null)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" disabled={busy} onClick={saveDetails}>
            Save details
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(pendingLeave)} onClose={() => setPendingLeave(null)}>
        <DialogTitle>Leave unsaved changes?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This consent has changes that have not been saved yet. They will be
            lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingLeave(null)}>Stay</Button>
          <Button
            onClick={async () => {
              const { action } = pendingLeave;
              setPendingLeave(null);
              if (await saveDraft({ silent: true })) action();
            }}
          >
            Save and continue
          </Button>
          <Button
            color="error"
            onClick={() => {
              const { action } = pendingLeave;
              setPendingLeave(null);
              setDirty(false);
              action();
            }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop open={busy || loading} sx={{ zIndex: 1200, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default ConsentBuilder;
